import { v1Client, useApi } from '@renderer/chat'
import { useAuth } from '@renderer/chat/providers'
import { deleteInsights } from '@shared/utils/insights'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Insight, InsightWithSource } from '@shared/utils/insights/types.ts'
import { useCallback } from 'react'
import OverlayIpcRenderer from '@renderer/ipc/OverlayIpc.renderer.ts'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer.ts'

const useBulkDeleteInsights = () => {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['delete-insights'],
    // perform the server + IPC delete
    mutationFn: async (insightIds: string[]) => {
      const token = await getAuthToken()
      const success = await deleteInsights(token, insightIds)
      if (!success) {
        throw new Error('Failed to delete insights from API')
      }
      return { deletedIds: insightIds }
    },

    // optimistic cache edit
    onMutate: async (insightIds: string[]) => {
      // cancel any outgoing refetches so we don’t overwrite our optimistic changes
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['insights'] }),
        queryClient.cancelQueries({ queryKey: ['todays-insights'] }),
      ])

      // snapshot previous cache values for rollback
      const prevInsights = queryClient.getQueryData<Insight[]>(['insights'])
      const prevTodays = queryClient.getQueryData<InsightWithSource[]>(['todays-insights'])

      // helper to filter out deleted IDs
      const keep = <T extends { id: string }>(arr?: T[]) => (arr ?? []).filter((i) => !insightIds.includes(i.id))

      // surgically update lists
      queryClient.setQueryData<Insight[]>(['insights'], (curr) => keep(curr))
      queryClient.setQueryData<InsightWithSource[]>(['todays-insights'], (curr) => keep(curr))

      // remove any per-insight detail caches to avoid stale reads
      for (const id of insightIds) {
        queryClient.removeQueries({ queryKey: ['insight', id], exact: true })
      }

      // context for rollback
      return { prevInsights, prevTodays }
    },

    // if the mutation fails, rollback to snapshots
    onError: (error, insightIds, ctx) => {
      console.error('Failure to delete insights:', insightIds, error)
      if (ctx?.prevInsights) {
        queryClient.setQueryData(['insights'], ctx.prevInsights)
      }
      if (ctx?.prevTodays) {
        queryClient.setQueryData(['todays-insights'], ctx.prevTodays)
      }
    },

    // success: we’re already in the right state; nothing to invalidate
    onSuccess: ({ deletedIds }) => {
      // ensure any per-insight caches are gone (in case they were created post-mutate)
      for (const id of deletedIds) {
        queryClient.removeQueries({ queryKey: ['insight', id], exact: true })
      }
    },
  })
}

const useUpdateInsight = () => {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['update-insight'],
    mutationFn: async ({
      insightId,
      updates,
    }: {
      insightId: string
      updates: { insight?: string; assignedBy?: string; status?: 'active' | 'completed'; sentimentScore?: number }
    }) => {
      const token = await getAuthToken()
      const response = await v1Client.PATCH('/insights/{id}', {
        params: {
          path: {
            id: insightId,
          },
        },
        body: updates,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.error) {
        throw new Error('Failed to update insight from API')
      }
    },
    onSuccess: (_, { insightId }) => {
      queryClient.invalidateQueries({ queryKey: ['insights'] })
      queryClient.invalidateQueries({ queryKey: ['todays-insights'] })
      queryClient.invalidateQueries({ queryKey: ['insight', insightId] })
    },
    onError: (error, insightId, updates) => {
      console.error('Failure to update insight:', insightId, updates, error)
    },
  })
}

type BulkPatchBody = {
  ids: string[]
  updates: {
    status?: Insight['status'] // 'active' | 'completed' (match your type)
    insight?: string
    assignedBy?: string
  }
}

const useBulkPatchInsights = () => {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['bulk-patch-insights'],

    // perform the server + IPC update
    mutationFn: async ({ ids, updates }: BulkPatchBody) => {
      const token = await getAuthToken()

      const response = await v1Client.PATCH('/insights', {
        body: ids.map((id) => {
          return {
            id,
            ...updates,
          }
        }),
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      })
      if ((response as any)?.error) {
        throw new Error('Failed to bulk patch insights from API')
      }

      return { updatedIds: ids, updates }
    },

    // optimistic cache edit
    onMutate: async (vars: BulkPatchBody) => {
      const { ids, ...updates } = vars

      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['insights'] }),
        queryClient.cancelQueries({ queryKey: ['todays-insights'] }),
      ])

      const prevInsights = queryClient.getQueryData<Insight[]>(['insights'])
      const prevTodays = queryClient.getQueryData<InsightWithSource[]>(['todays-insights'])

      const apply = (arr?: Insight[]) => (arr ?? []).map((i) => (ids.includes(i.id) ? { ...i, ...updates } : i))

      queryClient.setQueryData<Insight[]>(['insights'], (curr) => apply(curr))
      queryClient.setQueryData<InsightWithSource[]>(['todays-insights'], (curr) => apply(curr))

      // update any per-insight caches
      for (const id of ids) {
        queryClient.setQueryData<Insight>(['insight', id], (curr: any) => (curr ? { ...curr, ...updates } : curr))
      }

      return { prevInsights, prevTodays }
    },

    // rollback on failure
    onError: (_error, _vars, ctx) => {
      if (ctx?.prevInsights) queryClient.setQueryData(['insights'], ctx.prevInsights)
      if (ctx?.prevTodays) queryClient.setQueryData(['todays-insights'], ctx.prevTodays)
    },

    // post-success: invalidate to refetch authoritative data
    onSuccess: ({ updatedIds }) => {
      queryClient.invalidateQueries({ queryKey: ['insights'] })
      queryClient.invalidateQueries({ queryKey: ['todays-insights'] })
      for (const id of updatedIds) {
        queryClient.invalidateQueries({ queryKey: ['insight', id] })
      }
    },
  })
}

const useCopyInsightsToClipboard = () => {
  return useCallback((insights: InsightWithSource[], eventProperties?: Record<string, any>) => {
    let markdown = ''

    if (insights.length > 1) {
      for (const insight of insights) {
        markdown += '- ' + insight.insight + '\n'
      }
      markdown += '\nCaptured by HighlightAI.com'
    } else {
      markdown += insights[0].insight
    }

    OverlayIpcRenderer.writeTextToClipboard(markdown)

    AmplitudeRenderer.trackEvent('Insights Action', {
      action: 'Copy',
      type: insights[0].meetingNote ? 'Meeting note' : 'Screen',
      count: insights.length,
      ...(eventProperties ?? {}),
    })
  }, [])
}

interface UseAudioNoteInsightsOptions {
  enabled?: boolean
  pollingInterval?: number
  maxPollingDuration?: number
}

const useAudioNoteInsights = (conversationId: string | undefined, options: UseAudioNoteInsightsOptions = {}) => {
  const { get } = useApi()
  const {
    enabled = true,
    pollingInterval = 2000, // 2 seconds
    maxPollingDuration = 30000, // 30 seconds
  } = options

  const startTime = Date.now()

  return useQuery<Insight[], Error>({
    queryKey: ['audio-note-insights', conversationId],
    queryFn: async () => {
      if (!conversationId) return []

      const resp = await get('insights', { version: 'v1' })
      if (!resp.ok) {
        throw new Error(resp.statusText ?? `Unexpected ${resp.status} error`)
      }
      const json = await resp.json()
      if (!json.success) {
        throw new Error('Unexpected error')
      }

      // Filter insights for this specific audio note
      const audioNoteInsights = (json.data as Insight[]).filter(
        (insight) => insight.source === 'audio-note' && insight.sourceId === conversationId,
      )

      return audioNoteInsights
    },
    enabled: enabled && !!conversationId,
    refetchInterval: (query) => {
      const hasInsights = query.state.data && query.state.data.length > 0
      const hasExceededMaxDuration = Date.now() - startTime > maxPollingDuration

      // Stop polling if we have insights or exceeded max duration
      if (hasInsights || hasExceededMaxDuration) {
        return false
      }

      return pollingInterval
    },
    refetchIntervalInBackground: true,
  })
}

export {
  useBulkDeleteInsights,
  useBulkPatchInsights,
  useUpdateInsight,
  useCopyInsightsToClipboard,
  useAudioNoteInsights,
}
